import React, { useRef, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../api/axios';
import { formatIndianDate } from '../utils/format';
import ConfirmModal from './ConfirmModal';
import { useToast } from './ToastHost';

const Approvals = ({ user }) => {
  const location = useLocation();
  const toast = useToast();
  const normalizedRole = String(user?.role || '').trim().toUpperCase();
  const [approvals, setApprovals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [types, setTypes] = useState([]);
  const [actionBusy, setActionBusy] = useState(false);

  const [salesMappingPending, setSalesMappingPending] = useState([]);
  const [salesMappingBusy, setSalesMappingBusy] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmBusy, setConfirmBusy] = useState(false);
  const confirmActionRef = useRef(null);

  const openConfirm = (title, message, action) => {
    confirmActionRef.current = action;
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmOpen(true);
  };
  const [formData, setFormData] = useState({
    project_id: '',
    type: 'Proposal',
    remarks: ''
  });

  useEffect(() => {
    fetchData(1);
  }, []);

  useEffect(() => {
    if (location.state?.openForm && (normalizedRole === 'ADMIN' || normalizedRole === 'BDM' || normalizedRole === 'SALES')) {
      setShowForm(true);
    }
  }, [location.state, normalizedRole]);

  const fetchData = async (nextPage = page) => {
    try {
      const [approvalsRes, projectsRes, typesRes, mappingRes] = await Promise.all([
        api.get('/api/approvals', { params: { page: nextPage, limit } }),
        api.get('/api/projects', { params: { all: true } }),
        api.get('/api/approval-types'),
        user.role === 'ADMIN' ? api.get('/api/bdm-sales-mapping', { params: { status: 'PENDING' } }) : Promise.resolve({ data: [] })
      ]);

      const approvalsPayload = approvalsRes.data;
      if (approvalsPayload && typeof approvalsPayload === 'object' && Array.isArray(approvalsPayload.items)) {
        setApprovals(approvalsPayload.items);
        setTotal(approvalsPayload.total || 0);
        setPage(approvalsPayload.page || nextPage);
      } else {
        setApprovals(Array.isArray(approvalsPayload) ? approvalsPayload : []);
        setTotal(Array.isArray(approvalsPayload) ? approvalsPayload.length : 0);
        setPage(nextPage);
      }
      setProjects(projectsRes.data);
      setTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
      setSalesMappingPending(Array.isArray(mappingRes.data) ? mappingRes.data : []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const reviewSalesMapping = async (id, status) => {
    try {
      setSalesMappingBusy(true);
      await api.post(`/api/bdm-sales-mapping/${id}/review`, { status });
      toast.success(`Request ${status.toLowerCase()}`);
      fetchData(page);
    } catch (error) {
      console.error('Error reviewing request:', error);
      toast.error('Failed to update request');
    } finally {
      setSalesMappingBusy(false);
    }
  };

  const deleteApproval = async (approvalId) => {
    openConfirm('Delete approval', 'Delete this approval request?', async () => {
      try {
        setActionBusy(true);
        await api.delete(`/api/approvals/${approvalId}`);
        fetchData(1);
        toast.success('Approval deleted');
      } catch (error) {
        console.error('Error deleting approval:', error);
        toast.error('Failed to delete approval');
      } finally {
        setActionBusy(false);
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setActionBusy(true);
      await api.post('/api/approvals', formData);
      setShowForm(false);
      setFormData({
        project_id: '',
        type: 'Proposal',
        remarks: ''
      });
      fetchData(1);
      toast.success('Approval requested');
    } catch (error) {
      console.error('Error creating approval request:', error);
      toast.error('Failed to request approval');
    } finally {
      setActionBusy(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const updateApproval = async (approvalId, status, remarks) => {
    try {
      setActionBusy(true);
      await api.put(`/api/approvals/${approvalId}`, { status, remarks });
      fetchData(page);
      toast.success(`Approval ${status.toLowerCase()}`);
    } catch (error) {
      console.error('Error updating approval:', error);
      toast.error('Failed to update approval');
    } finally {
      setActionBusy(false);
    }
  };

  if (loading) {
    return <div className="text-center p-8">Loading approvals...</div>;
  }

  const totalPages = Math.max(Math.ceil(total / limit) || 1, 1);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Approvals</h1>
        {normalizedRole === 'SALES' && (
          <button
            onClick={() => setShowForm(true)}
            className="glass-button primary"
            disabled={actionBusy || confirmBusy}
          >
            + Request Approval
          </button>
        )}
      </div>

      {user.role === 'ADMIN' && salesMappingPending.length > 0 && (
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">BDM ↔ Sales Requests</h2>
            <span className="text-sm text-tertiary">Pending: {salesMappingPending.length}</span>
          </div>
          <div className="grid gap-3">
            {salesMappingPending.map((r) => (
              <div key={r.id} className="flex items-center justify-between">
                <div className="min-w-0">
                  <div className="font-medium">{r.bdm_name} → {r.sales_name}</div>
                  <div className="text-xs text-tertiary">{r.sales_email} • Requested: {formatIndianDate(r.requested_at)}</div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="glass-button success text-sm px-3 py-1"
                    onClick={() => reviewSalesMapping(r.id, 'APPROVED')}
                    disabled={salesMappingBusy || actionBusy || confirmBusy}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="glass-button danger text-sm px-3 py-1"
                    onClick={() => reviewSalesMapping(r.id, 'REJECTED')}
                    disabled={salesMappingBusy || actionBusy || confirmBusy}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="glass-card p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Request Approval</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Project</label>
              <select
                name="project_id"
                value={formData.project_id}
                onChange={handleChange}
                className="glass-select w-full"
                required
              >
                <option value="">Select Project</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.title} - {project.client_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Type</label>
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                className="glass-select w-full"
              >
                {(types.filter((t) => t.is_active === 1 || t.is_active === true)).map((t) => (
                  <option key={t.id} value={t.name}>{t.name}</option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-2">Remarks</label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                className="glass-textarea w-full"
                rows="3"
                placeholder="Additional notes or context for this approval request..."
              />
            </div>

            <div className="col-span-2 flex gap-2">
              <button type="submit" className="glass-button primary">
                Submit Request
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="glass-button"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid gap-4">
        {approvals.map((approval) => (
          <div key={approval.id} className="glass-card p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold">
                  {approval.type} Approval
                </h3>
                <p className="text-secondary">Project: {approval.project_title}</p>
                <p className="text-sm text-tertiary">
                  Requested by: {approval.requested_by_name}
                </p>
                {approval.approved_by_name && (
                  <p className="text-sm text-tertiary">
                    Approved by: {approval.approved_by_name}
                  </p>
                )}
              </div>
              <span className={`status-badge approval-${approval.status.toLowerCase()}`}>
                {approval.status}
              </span>
            </div>

            {approval.remarks && (
              <div className="mb-4">
                <p className="text-sm text-secondary mb-1">Remarks:</p>
                <p className="text-sm">{approval.remarks}</p>
              </div>
            )}

            <div className="flex justify-between items-center">
              <span className="text-xs text-tertiary">
                Created: {formatIndianDate(approval.created_at)}
              </span>
              <div className="flex gap-2">
                {(user.role === 'ADMIN' || user.role === 'BDM') && approval.status === 'Pending' && (
                  <>
                    <button
                      onClick={() => updateApproval(approval.id, 'Approved', user.role === 'ADMIN' ? 'Approved by admin' : 'Approved by BDM')}
                      className="glass-button success text-sm px-3 py-1"
                      disabled={actionBusy || confirmBusy}
                    >
                      Approve
                    </button>
                    <button
                      onClick={() => updateApproval(approval.id, 'Rejected', user.role === 'ADMIN' ? 'Rejected by admin' : 'Rejected by BDM')}
                      className="glass-button danger text-sm px-3 py-1"
                      disabled={actionBusy || confirmBusy}
                    >
                      Reject
                    </button>
                  </>
                )}

                {user.role === 'ADMIN' && (
                  <button
                    type="button"
                    className="glass-button danger text-sm px-3 py-1"
                    onClick={() => deleteApproval(approval.id)}
                    disabled={actionBusy || confirmBusy}
                  >
                    Delete
                  </button>
                )}

                {user.role === 'BDM' && approval.status === 'Pending' && approval.requested_by === user.id && (
                  <button
                    type="button"
                    className="glass-button danger text-sm px-3 py-1"
                    onClick={() => deleteApproval(approval.id)}
                    disabled={actionBusy || confirmBusy}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        confirmText="Delete"
        cancelText="Cancel"
        busy={confirmBusy}
        onCancel={() => {
          if (confirmBusy) return;
          setConfirmOpen(false);
        }}
        onConfirm={async () => {
          const action = confirmActionRef.current;
          if (!action) {
            setConfirmOpen(false);
            return;
          }
          try {
            setConfirmBusy(true);
            await action();
            setConfirmOpen(false);
          } finally {
            setConfirmBusy(false);
          }
        }}
      />

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-tertiary">
            Page {page} of {totalPages} ({total} approvals)
          </div>
          <div className="flex gap-2">
            <button type="button" className="glass-button" onClick={() => fetchData(page - 1)} disabled={!canPrev}>
              Prev
            </button>
            <button type="button" className="glass-button" onClick={() => fetchData(page + 1)} disabled={!canNext}>
              Next
            </button>
          </div>
        </div>
      )}

      {approvals.length === 0 && (
        <div className="text-center p-8 glass-card">
          <p className="text-secondary">No approval requests found</p>
        </div>
      )}
    </div>
  );
};

export default Approvals;
